/*
  Free gift auto-claim for the cart drawer.

  Works with snippets/free-gift-bar.liquid (renders the milestone bar and a
  JSON config) and assets/free-gift.css. The gift itself is made free at
  checkout by Shopify automatic discounts (see FREE-GIFT-SETUP.md) — this
  script only adds/removes the gift line.

  Responsibilities:
  - Auto-add: when the qualifying subtotal reaches a product tier and no
    gift line for that tier is in the cart, add a random available variant
    of the configured gift pool with the hidden `_free_gift: tierN` line
    property. No claim button — the gift just appears in the cart.
  - Auto-remove: drop a claimed gift when the qualifying subtotal falls
    back under its tier threshold (so nobody is charged for a "free" gift).
  - Respect removal: when the customer clicks Remove on a gift line the
    tier is marked declined for the session (event-driven; inferring the
    removal from cart state would misfire after checkout or cart-clear)
    and re-arms only after the subtotal drops back below the threshold
    (or in a new session).
  - Refresh the cart drawer the same way the theme does (section render).
*/

(function () {
  'use strict';

  var SECTION_ID = 'quick-cart';
  var SELECTORS = {
    bar: '[data-free-gift-bar]',
    config: '[data-free-gift-config]',
    drawerItems: '.quick-cart__items',
    drawerSubtotal: '.quick-cart__footer-subtotal span',
    drawerDiscounts: '.quick-cart__item-discounts'
  };

  var DECLINED_KEY_PREFIX = 'paFreeGiftDeclined:';

  var busy = false;
  // If a cart change arrives while we're mid add/remove, don't drop it — flag
  // it and re-reconcile from fresh state once the in-flight op settles.
  var pendingSync = false;

  /* ---------------------------------------------------------------- utils */

  function getConfig(scope) {
    var el = (scope || document).querySelector(SELECTORS.config);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return null;
    }
  }

  function storageGet(key) {
    try { return window.sessionStorage.getItem(key); } catch (err) { return null; }
  }

  function storageSet(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (err) {}
  }

  function storageRemove(key) {
    try { window.sessionStorage.removeItem(key); } catch (err) {}
  }

  function cartRequest(path, payload) {
    return fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok) {
        return res.json().then(function (data) {
          throw new Error((data && (data.description || data.message)) || 'Cart error');
        });
      }
      return res.json();
    });
  }

  function refreshDrawer() {
    var root = (window.theme && window.theme.routes && window.theme.routes.cart.base) || '/cart';
    return fetch(root + '?section_id=' + SECTION_ID)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = document.createElement('div');
        doc.innerHTML = html;
        [SELECTORS.drawerItems, SELECTORS.drawerSubtotal, SELECTORS.drawerDiscounts].forEach(function (sel) {
          var fresh = doc.querySelector(sel);
          var current = document.querySelector('.quick-cart ' + sel) || document.querySelector(sel);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
        // Header cart count bubbles elsewhere on the page.
        var freshCount = doc.querySelector('[data-js-cart-count]');
        if (freshCount) {
          document.querySelectorAll('[data-js-cart-count]').forEach(function (node) {
            node.innerHTML = freshCount.innerHTML;
          });
        }
      });
  }

  /* ------------------------------------------------------- gift lifecycle */

  function pickRandomVariant(tier) {
    var available = (tier.variants || []).filter(function (v) { return v.available; });
    if (!available.length) return null;
    return available[Math.floor(Math.random() * available.length)];
  }

  function addGift(variantId, tierKey) {
    var properties = {};
    properties._free_gift = tierKey;
    return cartRequest('/cart/add.js', {
      items: [{ id: variantId, quantity: 1, properties: properties }]
    });
  }

  function removeGiftLine(lineKey) {
    return cartRequest('/cart/change.js', { id: lineKey, quantity: 0 });
  }

  // Live cart state (qualifying total + claimed gift lines) from a cart JSON
  // payload, e.g. the theme's cart:updated event detail. Lets us act before
  // the drawer's Liquid config has been re-rendered.
  function cartStateFromCartJson(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    var qualifying = 0;
    var claimed = [];
    cart.items.forEach(function (item) {
      var tierKey = item.properties && item.properties._free_gift;
      if (tierKey) {
        claimed.push({ tier: tierKey, lineKey: item.key });
      } else {
        qualifying += Number(item.final_line_price || item.line_price || 0);
      }
    });
    return { qualifyingTotal: qualifying, claimed: claimed };
  }

  // Reconcile the cart with the tier state. Tier definitions come from the
  // Liquid-rendered config; the cart side can be overridden with fresher
  // data (cart:updated event) so we don't wait for the section re-render.
  function syncGifts(liveState) {
    if (busy) { pendingSync = true; return; }
    var config = getConfig();
    if (!config || !config.tiers || !config.tiers.length) return;

    var qualifyingTotal = liveState ? liveState.qualifyingTotal : config.qualifyingTotal;
    var claimedList = liveState ? liveState.claimed : config.claimed;

    var claimedByTier = {};
    (claimedList || []).forEach(function (claim) {
      claimedByTier[claim.tier] = claim;
    });

    var removeKeys = [];
    var addItems = [];

    config.tiers.forEach(function (tier) {
      var claim = claimedByTier[tier.key];
      var reached = qualifyingTotal >= tier.threshold;

      if (!reached) {
        // Below the threshold: drop a lingering gift line and re-arm the tier.
        storageRemove(DECLINED_KEY_PREFIX + tier.key);
        if (claim) removeKeys.push(claim.lineKey);
        return;
      }

      if (claim) return;

      // Declined tiers (customer clicked Remove on the gift line) stay off
      // until the subtotal drops back below the threshold.
      if (storageGet(DECLINED_KEY_PREFIX + tier.key)) return;

      var winner = pickRandomVariant(tier);
      if (!winner) return;
      addItems.push({ id: winner.id, quantity: 1, properties: { _free_gift: tier.key } });
    });

    if (!removeKeys.length && !addItems.length) return;

    busy = true;
    Promise.resolve()
      .then(function () {
        if (!removeKeys.length) return;
        // Remove ALL lingering gift lines in ONE atomic /cart/update.js. Shopify
        // re-keys the remaining lines after each /cart/change.js, so removing two
        // gifts with sequential per-line calls left the 2nd key stale ("no valid
        // id or line parameter") — that gift stayed in the cart and, being below
        // its tier, lost its discount and got charged (e.g. a $49 cart checking
        // out at $66.99). A single update keyed by line-key applies every removal
        // against one consistent cart state.
        var updates = {};
        removeKeys.forEach(function (key) { updates[key] = 0; });
        return cartRequest('/cart/update.js', { updates: updates });
      })
      .then(function () {
        if (!addItems.length) return;
        return cartRequest('/cart/add.js', { items: addItems });
      })
      .then(refreshDrawer)
      .catch(function () { /* leave cart as-is; discounts protect pricing */ })
      .then(function () {
        busy = false;
        // A change came in mid-flight (or our own op shifted the tier state) —
        // reconcile once more against the freshly rendered cart config.
        if (pendingSync) { pendingSync = false; syncGifts(); }
      });
  }

  /* --------------------------------------------------------------- wiring */

  // Customer explicitly removes a gift line (drawer or cart page): mark the
  // tier declined so the auto-add doesn't fight them. Capture phase so we
  // see the click even though the theme re-renders the line right after.
  document.addEventListener('click', function (event) {
    var removeControl = event.target && event.target.closest
      ? event.target.closest('[data-remove-item]') : null;
    if (!removeControl) return;
    var lineEl = removeControl.closest('[data-input-item]');
    var lineKey = lineEl && lineEl.getAttribute('data-key');
    if (!lineKey) return;
    var config = getConfig();
    if (!config || !config.claimed) return;
    config.claimed.forEach(function (claim) {
      if (claim.lineKey === lineKey) {
        storageSet(DECLINED_KEY_PREFIX + claim.tier, '1');
      }
    });
  }, true);

  var scheduled = null;
  function scheduleSync(delay) {
    if (scheduled) return;
    scheduled = setTimeout(function () {
      scheduled = null;
      syncGifts();
    }, delay);
  }

  // Theme cart events fire as soon as the cart POST returns — reacting here
  // lets the gift add/remove run in parallel with the theme's own drawer
  // re-render instead of being serialized after the DOM swap. When the
  // event carries the fresh cart JSON we act on it immediately; otherwise
  // (and as a convergence fallback) the MutationObserver path re-syncs from
  // the re-rendered Liquid config.
  document.addEventListener('cart:updated', function (event) {
    var liveState = cartStateFromCartJson(event.detail && event.detail.cart);
    if (liveState) {
      syncGifts(liveState);
    } else {
      scheduleSync(50);
    }
  });
  document.addEventListener('quick-cart:updated', function () { scheduleSync(50); });

  // Fallback: the theme swaps the drawer's innerHTML on every cart change;
  // re-check tier state whenever a fresh bar shows up.
  var observer = new MutationObserver(function () { scheduleSync(150); });

  function init() {
    var drawer = document.querySelector('.quick-cart');
    if (drawer) {
      observer.observe(drawer, { childList: true, subtree: true });
    }
    syncGifts();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
